import { useState } from 'react';
import { useAuth } from '@/hooks/auth/use-auth';
import axios from 'axios';
import { Eye, EyeOff, Loader2, Lock, User, Building, Shield, ArrowRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

interface School {
  schoolName: string;
  schoolBackendUrl: string;
}

interface SchoolOption {
  value: School;
  label: string;
}

const LOCAL_BACKEND_URL = 'http://127.0.0.1:8001';
// Header the dashboard sends to the deployed Goreeva-School test backend
// (Goreeva-School -> https://testapi.goreeva.com, identifier "testapi").
// Local requests carry the same identifier so cache namespaces and any
// school-gating match the test deployment exactly.
const LOCAL_SCHOOL_IDENTIFIER = 'testapi';

const LOCAL_BACKEND_OPTION: SchoolOption = {
  value: { schoolName: 'Local Backend', schoolBackendUrl: LOCAL_BACKEND_URL },
  label: 'Local Backend (dev only)',
};

const SCHOOLS: School[] = [
  {
    schoolName: 'Shree Swaminarayan English Medium School - Nashik',
    schoolBackendUrl: 'https://ssnemsapi.goreeva.com',
  },
  { schoolName: 'Goreeva-School', schoolBackendUrl: 'https://testapi.goreeva.com' },
  {
    schoolName: 'Golden Days Universal School Umrale',
    schoolBackendUrl: 'https://gdusumapi.goreeva.com',
  },
  {
    schoolName: 'Golden Days Universal School Ozar',
    schoolBackendUrl: 'https://gdusozarapi.goreeva.com',
  },
  {
    schoolName: 'Golden Days Universal School Dindori',
    schoolBackendUrl: 'https://gdusdindoriapi.goreeva.com',
  },
  {
    schoolName: 'Golden Days Universal School Nashik',
    schoolBackendUrl: 'https://gdustapovanapi.goreeva.com',
  },
  {
    schoolName: 'Golden Days Universal School HAL',
    schoolBackendUrl: 'https://gdushalapi.goreeva.com',
  },
];

const schoolOptions: SchoolOption[] = [
  ...(import.meta.env.DEV ? [LOCAL_BACKEND_OPTION] : []),
  ...SCHOOLS.map((school) => ({ value: school, label: school.schoolName })),
];

export default function LoginPage() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<SchoolOption | null>(null);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSchool) {
      toast.error('Please select a school');
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.post(`${selectedSchool.value.schoolBackendUrl}/api/token/login/`, {
        password: formData.password,
        username: formData.username,
      });
      const backendUrl = selectedSchool.value.schoolBackendUrl;
      localStorage.setItem('schoolBackendURL', backendUrl);
      localStorage.setItem('schoolName', selectedSchool.value.schoolName);
      // Extract school identifier from URL (e.g., "ssnemsapi" from "https://ssnemsapi.goreeva.com").
      // The local backend always identifies as the Goreeva-School test deployment.
      const schoolIdentifier =
        backendUrl === LOCAL_BACKEND_URL
          ? LOCAL_SCHOOL_IDENTIFIER
          : backendUrl.split('//')[1].split('.goreeva.com')[0];
      localStorage.setItem('schoolIdentifier', schoolIdentifier);
      login(res.data.access, res.data.refresh);
    } catch {
      console.warn('Login failed');
      toast.error('Invalid username or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden">
      {/* Enhanced Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        {/* Animated background elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-[#5777BA]/8 to-blue-400/8 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-gradient-to-r from-indigo-400/8 to-[#5777BA]/8 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-gradient-to-r from-blue-300/6 to-slate-300/6 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
                <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#5777BA" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex">
        {/* Left Side - Brand & Info */}
        <div className="hidden lg:flex lg:w-2/5 xl:w-1/2 flex-col justify-between p-12 relative">
          {/* Brand Section */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#5777BA] to-[#3f5a9e] rounded-xl flex items-center justify-center shadow-lg">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-[#5777BA] to-[#3f5a9e] bg-clip-text text-transparent">
                    GOREEVA
                  </h1>
                  <p className="text-slate-600 text-sm">School Management System</p>
                </div>
              </div>
            </div>

            {/* Feature highlights */}
            <div className="space-y-6 max-w-md">
              <div className="backdrop-blur-sm bg-white/40 border border-white/60 rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Secure & Reliable</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Advanced security protocols ensure your school data remains protected with
                  enterprise-grade encryption.
                </p>
              </div>

              <div className="backdrop-blur-sm bg-white/40 border border-white/60 rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Easy Management</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Streamlined interface designed for educators to manage students, classes, and
                  administrative tasks efficiently.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom decoration */}
          <div className="relative">
            <div className="w-24 h-1 bg-gradient-to-r from-[#5777BA] to-transparent rounded-full"></div>
            <p className="text-slate-500 text-xs mt-4">
              Trusted by educational institutions worldwide
            </p>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 lg:w-3/5 xl:w-1/2 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            {/* Form Container */}
            <div className="backdrop-blur-xl bg-white/70 border border-white/80 rounded-3xl p-8 shadow-2xl shadow-[#5777BA]/5">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#5777BA] to-[#3f5a9e] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Welcome back</h2>
                <p className="text-slate-600">Sign in to access your dashboard</p>
              </div>

              {/* Form */}
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Building className="h-4 w-4 text-[#5777BA]" />
                    School
                  </label>
                  <Select
                    disabled={isLoading}
                    value={selectedSchool?.value?.schoolName}
                    onValueChange={(value) => {
                      const school = schoolOptions.find((s) => s.value.schoolName === value);
                      setSelectedSchool(school || null);
                      if (school) {
                        try {
                          localStorage.setItem('schoolName', school.value.schoolName);
                        } catch {
                          // noop: localStorage not available
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="h-12 rounded-xl border-slate-200/60 bg-white/60 backdrop-blur-sm focus:ring-[#5777BA]/30 focus:border-[#5777BA] focus:bg-white/80 transition-all">
                      <SelectValue placeholder="Select your school" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[var(--radix-select-trigger-width)] backdrop-blur-xl bg-white/90 border-white/60">
                      {schoolOptions.map((school) => (
                        <SelectItem
                          key={school.value.schoolBackendUrl}
                          value={school.value.schoolName}
                          className="whitespace-normal break-words"
                        >
                          {school.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <User className="h-4 w-4 text-[#5777BA]" />
                    Username
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))}
                    className="flex h-12 w-full rounded-xl border border-slate-200/60 bg-white/60 backdrop-blur-sm px-4 py-2 text-sm transition-all focus:border-[#5777BA] focus:bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#5777BA]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Enter your username"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Lock className="h-4 w-4 text-[#5777BA]" />
                      Password
                    </label>
                    <a
                      href="#"
                      tabIndex={-1}
                      className="text-xs text-[#5777BA] hover:text-[#3f5a9e] hover:underline transition-colors"
                    >
                      Forgot password?
                    </a>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, password: e.target.value }))
                      }
                      className="flex h-12 w-full rounded-xl border border-slate-200/60 bg-white/60 backdrop-blur-sm px-4 py-2 text-sm transition-all focus:border-[#5777BA] focus:bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#5777BA]/20 disabled:opacity-50 disabled:cursor-not-allowed pr-12"
                      placeholder="Enter your password"
                      required
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-[#5777BA] to-[#3f5a9e] text-white font-medium text-sm transition-all duration-200 hover:shadow-lg hover:shadow-[#5777BA]/25 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#5777BA]/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 mt-8"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign in to Dashboard
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>

              {/* Footer */}
              <div className="mt-8 text-center">
                <p className="text-sm text-slate-500">
                  Need help?{' '}
                  <a
                    href="#"
                    className="text-[#5777BA] hover:text-[#3f5a9e] hover:underline transition-colors font-medium"
                  >
                    Contact support
                  </a>
                </p>
              </div>
            </div>

            {/* Mobile brand info */}
            <div className="lg:hidden mt-8 text-center">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-[#5777BA] to-[#3f5a9e] rounded-lg flex items-center justify-center">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <span className="text-lg font-bold bg-gradient-to-r from-[#5777BA] to-[#3f5a9e] bg-clip-text text-transparent">
                  GOREEVA
                </span>
              </div>
              <p className="text-slate-600 text-sm">School Management System</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
